import { Fragment } from 'react';
import { Button, Menu, MenuItem, MenuTrigger, Popover, Separator } from 'react-aria-components';
import { MoreHorizontal } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useT } from '../../../i18n/hooks';
import { FM } from './messages';
import { cx } from './parts';
import type { FieldSize } from './types';
import './styles';

export interface RowMenuItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  onAction: () => void;
  isDisabled?: boolean;
  /** Необратимое действие (удалить): цвет «тревога», а не красная заливка. */
  tone?: 'default' | 'danger';
  /** Линия над пунктом: отделяет опасные действия от обычных. */
  separatorBefore?: boolean;
}

export interface RowMenuProps {
  items: readonly RowMenuItem[];
  /** Что за строка: «Действия: кампания Культивация». По умолчанию «Действия». */
  'aria-label'?: string;
  size?: FieldSize;
  className?: string;
}

/**
 * Меню действий строки «⋯». В строке остаётся одно контекстное действие,
 * остальное — здесь. Вся клавиатура (стрелки, Home/End, набор букв, Escape,
 * возврат фокуса на кнопку) — React Aria.
 */
export function RowMenu({ items, 'aria-label': ariaLabel, size = 'md', className }: RowMenuProps) {
  const t = useT();
  return (
    <MenuTrigger>
      <Button className={cx('fm-rowmenu', className)} data-size={size} aria-label={ariaLabel ?? t(FM.rowMenu)}>
        <MoreHorizontal size={18} aria-hidden />
      </Button>
      <Popover className="ac-popover fm-popover fm-menu-popover" placement="bottom end">
        <Menu className="fm-menu" onAction={(key) => items.find((item) => item.id === key)?.onAction()}>
          {items.map((item) => (
            <Fragment key={item.id}>
              {item.separatorBefore ? <Separator className="fm-menu-sep" /> : null}
              <MenuItem id={item.id} className="fm-menu-item" textValue={item.label} isDisabled={item.isDisabled} data-danger={item.tone === 'danger' ? '' : undefined}>
                {item.icon ? <item.icon size={16} aria-hidden /> : null}
                {item.label}
              </MenuItem>
            </Fragment>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
